/**
 * WelcomeTourPage — compact one-screen welcome shown on first login.
 *
 * Route: /welcome
 * Shown automatically after first login instead of the full /quickstart guide.
 * 4 cards, no screenshots, no scrolling wall.
 * CTA buttons mark quickstart seen server-side + localStorage before navigating.
 */
import { useNavigate } from 'react-router-dom'
import { Mic, Wand2, FileText, Brain, ArrowRight, BookOpen } from 'lucide-react'
import { useStore } from '../lib/store'

interface TourCard {
  icon: React.ReactNode
  title: string
  body: string
}

const CARDS: TourCard[] = [
  {
    icon: <Mic size={22} />,
    title: 'Hit Capture and talk',
    body: "Tap Capture, say the patient's name and visit type — the AI handles the rest. Works offline too.",
  },
  {
    icon: <Wand2 size={22} />,
    title: 'Direct it with your voice',
    body: '"Use my usual knee exam", "same exam as last time", "scratch that — 20 milligrams". Commands just work.',
  },
  {
    icon: <FileText size={22} />,
    title: 'Review, polish, approve',
    body: 'Every line is quality-checked against what you actually said. Nothing is invented.',
  },
  {
    icon: <Brain size={22} />,
    title: 'It learns your style',
    body: 'Every edit and revision teaches it your preferences. The more you use it, the less you edit.',
  },
]

export function WelcomeTourPage() {
  const navigate = useNavigate()
  const { markQuickstartSeen } = useStore()

  function handleStartVisit() {
    markQuickstartSeen()
    navigate('/')
  }

  function handleFullGuide() {
    markQuickstartSeen()
    navigate('/quickstart')
  }

  return (
    <div className="max-w-xl mx-auto px-4 pt-8 pb-16">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Welcome to Dr. Scribey 👋
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Here's everything you need to know to get started.
        </p>
      </div>

      {/* 4 compact cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {CARDS.map((card, i) => (
          <div
            key={i}
            className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5"
          >
            <div className="w-10 h-10 rounded-xl bg-brand-500/15 text-brand-500 flex items-center justify-center mb-3">
              {card.icon}
            </div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {card.title}
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              {card.body}
            </p>
          </div>
        ))}
      </div>

      {/* CTAs */}
      <div className="flex flex-col items-center gap-3">
        <button
          onClick={handleStartVisit}
          className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-8 py-4 rounded-full font-bold text-base shadow-xl transition-all hover:scale-105 active:scale-95"
        >
          Start my first visit
          <ArrowRight size={18} />
        </button>
        <button
          onClick={handleFullGuide}
          className="inline-flex items-center gap-1.5 text-sm text-brand-500 hover:text-brand-400 transition-colors"
        >
          <BookOpen size={14} />
          Read the full guide (5 min)
        </button>
      </div>
    </div>
  )
}
